import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import PollModal from '../components/PollModal';
import { MdSearch, MdClear, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

function AdminPolls() {
  const navigate = useNavigate();
  const [polls, setPolls] = useState([]);
  const [tags, setTags] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Search and filter state
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isTagMenuCollapsed, setIsTagMenuCollapsed] = useState(true);
  const [showAllTags, setShowAllTags] = useState(false);

  // Memoized filtered polls
  const filteredPolls = useMemo(() => {
    let result = [...polls];
    
    // Apply keyword search
    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase();
      result = result.filter(poll => 
        poll.question.toLowerCase().includes(keyword)
      );
    }
    
    // Apply tag filter
    if (selectedTags.length > 0) {
      result = result.filter(poll => 
        selectedTags.some(tagId => poll.tags.includes(tagId))
      );
    }
    
    return result;
  }, [polls, searchKeyword, selectedTags]);

  // Get popular tags (first 10)
  const popularTags = useMemo(() => tags.slice(0, 10), [tags]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [pollsRes, tagsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/polls`),
          axios.get(`${API_URL}/api/tags`)
        ]);
        setPolls(pollsRes.data.polls || []);
        setTags(tagsRes.data || []);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const loadPolls = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/polls`);
      setPolls(response.data.polls || []);
    } catch (error) {
      console.error('Failed to load polls:', error);
    }
  };

  const handleDelete = async (pollId) => {
    if (!window.confirm('Are you sure you want to delete this poll?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/polls/${pollId}`);
      loadPolls();
    } catch (error) {
      alert('Failed to delete poll');
    }
  };

  // Sanitize search input to prevent XSS and SQL injection
  const sanitizeSearchInput = (input) => {
    let sanitized = input
      .replace(/[<>'`;(){}[\]\\]/g, '')
      .replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|TRUNCATE|EXEC|EXECUTE)\b)/gi, '')
      .replace(/--/g, '')
      .replace(/\/\*/g, '')
      .replace(/\*\//g, '')
      .trim();
    return sanitized.substring(0, 50);
  };

  const handleSearchInputChange = (e) => {
    const sanitized = sanitizeSearchInput(e.target.value);
    setSearchInput(sanitized);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchKeyword(sanitizeSearchInput(searchInput));
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchKeyword('');
  };

  const toggleTag = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchKeyword('');
    setSelectedTags([]);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 font-rubik">Manage Polls</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-6 py-2"
          data-testid="create-poll-button"
        >
          Create Poll
        </button>
      </div>

      {/* Search & Filter Section */}
      <div className="card-container mb-6" data-testid="admin-search-filter-section">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 font-rubik">Search & Filter</h3>
          <button
            onClick={clearFilters}
            disabled={selectedTags.length === 0 && !searchKeyword}
            className={`text-sm font-medium font-rubik transition-colors ${
              selectedTags.length > 0 || searchKeyword
                ? 'text-danger hover:text-red-800 cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            data-testid="admin-clear-filters-button"
          >
            Clear All
          </button>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search polls by keyword..."
              value={searchInput}
              onChange={handleSearchInputChange}
              maxLength={50}
              className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-secondary focus:border-secondary font-rubik text-sm"
              data-testid="admin-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="admin-clear-search-btn"
              >
                <MdClear className="text-xl" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-6 py-3 bg-secondary text-white rounded-card font-rubik font-medium hover:bg-secondary/90 transition-colors"
            data-testid="admin-search-btn"
          >
            Search
          </button>
        </form>

        {/* Active Filters Display */}
        {(searchKeyword || selectedTags.length > 0) && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-card" data-testid="admin-active-filters-display">
            <h4 className="text-xs font-semibold text-gray-500 mb-2 font-rubik uppercase tracking-wide">Active Filters</h4>
            <div className="flex flex-wrap gap-2">
              {searchKeyword && (
                <span className="px-3 py-1.5 bg-secondary/10 text-secondary rounded-full text-sm font-medium font-rubik flex items-center gap-2">
                  <MdSearch className="text-sm" />
                  &ldquo;{searchKeyword}&rdquo;
                  <button onClick={clearSearch} className="hover:text-red-600 ml-1">×</button>
                </span>
              )}
              {selectedTags.map(tagId => {
                const tag = tags.find(t => t.tagId === tagId);
                if (!tag) return null;
                return (
                  <button
                    key={tag.tagId}
                    onClick={() => toggleTag(tag.tagId)}
                    className="px-3 py-1.5 rounded-full text-sm font-medium text-white hover:opacity-80 transition-all flex items-center gap-1"
                    style={{ backgroundColor: tag.color }}
                    data-testid={`admin-selected-tag-${tag.name}`}
                  >
                    {tag.name} ×
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tag Filters - Collapsible */}
        {tags.length > 0 && (
          <>
            <button
              onClick={() => setIsTagMenuCollapsed(!isTagMenuCollapsed)}
              className="w-full flex items-center justify-between py-2 text-gray-700 font-rubik font-medium hover:text-primary transition-colors"
              data-testid="admin-toggle-tag-menu"
            >
              <span className="flex items-center gap-2">
                {isTagMenuCollapsed ? <MdKeyboardArrowDown className="text-xl" /> : <MdKeyboardArrowUp className="text-xl" />}
                Filter by Tags
              </span>
            </button>

            {/* Collapsible Tag Content */}
            {!isTagMenuCollapsed && (
              <div className="pt-3 border-t mt-2">
                {/* Popular Tags */}
                {popularTags.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-gray-500 mb-2 font-rubik uppercase tracking-wide">Popular Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {popularTags.filter(tag => !selectedTags.includes(tag.tagId)).map(tag => (
                        <button
                          key={tag.tagId}
                          onClick={() => toggleTag(tag.tagId)}
                          className="px-3 py-1.5 rounded-full text-sm font-medium text-white hover:opacity-90 transition-all"
                          style={{ backgroundColor: tag.color }}
                          data-testid={`admin-tag-quick-${tag.name}`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* View All Tags Button */}
                <button
                  onClick={() => setShowAllTags(!showAllTags)}
                  className="w-full bg-civote-bg hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-card font-medium transition-all flex items-center justify-between font-rubik text-sm"
                  data-testid="admin-toggle-tag-dropdown"
                >
                  <span>{showAllTags ? 'Hide All Tags' : `View All Tags (${tags.length})`}</span>
                  {showAllTags ? <MdKeyboardArrowUp className="text-xl" /> : <MdKeyboardArrowDown className="text-xl" />}
                </button>

                {/* All Tags Dropdown */}
                {showAllTags && (
                  <div className="mt-3 max-h-64 overflow-y-auto" data-testid="admin-all-tags-scrollable">
                    <div className="flex flex-wrap gap-2">
                      {tags.filter(tag => !selectedTags.includes(tag.tagId)).map(tag => (
                        <button
                          key={tag.tagId}
                          onClick={() => toggleTag(tag.tagId)}
                          className="px-3 py-1 rounded-full text-sm font-medium text-white hover:opacity-90 transition-all"
                          style={{ backgroundColor: tag.color }}
                          data-testid={`admin-tag-filter-${tag.name}`}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Results Count */}
      {!loading && (
        <div className="mb-4 text-sm text-gray-600 font-rubik">
          Showing {filteredPolls.length} of {polls.length} polls
          {(searchKeyword || selectedTags.length > 0) && ' (filtered)'}
        </div>
      )}

      {/* Polls List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        </div>
      ) : filteredPolls.length === 0 ? (
        <div className="card-container text-center">
          <p className="text-gray-500 text-lg font-rubik">
            {polls.length === 0 
              ? 'No polls found. Create your first poll!' 
              : 'No polls match your search criteria.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPolls.map(poll => (
            <div key={poll.pollId} className="card-container" data-testid={`admin-poll-${poll.pollId}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2 font-rubik">{poll.question}</h3>
                  <div className="flex flex-wrap gap-2">
                    {poll.tags.map(tagId => {
                      const tag = tags.find(t => t.tagId === tagId);
                      return tag ? (
                        <span
                          key={tagId}
                          className="px-3 py-1 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/admin/polls/${poll.pollId}/responses`)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-rubik"
                    data-testid={`view-responses-${poll.pollId}`}
                  >
                    View Responses
                  </button>
                  <button
                    onClick={() => setEditingPoll(poll)}
                    className="px-4 py-2 btn-tertiary text-sm font-rubik"
                    data-testid={`edit-poll-${poll.pollId}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(poll.pollId)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-rubik"
                    data-testid={`delete-poll-${poll.pollId}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreateModal || editingPoll) && (
        <PollModal
          poll={editingPoll}
          tags={tags}
          onClose={() => {
            setShowCreateModal(false);
            setEditingPoll(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingPoll(null);
            loadPolls();
          }}
        />
      )}
    </div>
  );
}

export default AdminPolls;
